/**
 * 种子数据：《中国近现代史纲要》(2023年版) 第十章「中国特色社会主义进入新时代」。
 *
 * 骨架来自教材，视野放大到世界：中国是主分支（中央主干），
 * 美国 / 俄罗斯 / 欧盟 等是各自颜色的分支，「世界」作为全球性事件的汇总分支。
 *
 * 注意：**每个事件用的是它自己发生的真实日期**，不是所属教材小节的时间点日期。
 * 这样时间轴上才会自然分出先后（例如 2022-02-24 俄乌冲突 与 2022-10-16 党的二十大
 * 分属两条水平线），而不是把同一小节的几个事件强行压在同一行。
 * 后期补充同期世界事件时，只需往 seedEvents 里加条目并指定 countryId。
 */
import type { Country, TimelineEvent } from '../types';

export const seedCountries: Country[] = [
  // order 的奇偶决定分支在主干哪一侧：奇数在右、偶数在左
  { id: 'china', name: '中国', color: '#dc2626', order: 0, isMain: true },
  { id: 'usa', name: '美国', color: '#2563eb', order: 1 },
  { id: 'russia', name: '俄罗斯', color: '#7c3aed', order: 2 },
  { id: 'eu', name: '欧盟', color: '#0284c7', order: 3 },
  { id: 'world', name: '世界', color: '#0d9488', order: 4 },
  { id: 'japan', name: '日本', color: '#e11d48', order: 5 },
  { id: 'uk', name: '英国', color: '#4f46e5', order: 6 },
  { id: 'korea', name: '韩国', color: '#0891b2', order: 7 },
];

const AT = (id: string, minute: number): { createdAt: string; updatedAt: string } => {
  const stamp = `2026-01-05T09:${String(minute).padStart(2, '0')}:00.000Z`;
  return { createdAt: stamp, updatedAt: stamp };
};

export const seedEvents: TimelineEvent[] = [
  /* ------------------------------ 2012 ------------------------------ */
  {
    id: 'e-2012-cn',
    source: 'textbook',
    title: '党的十八大胜利召开',
    countryId: 'china',
    detail: [
      '**会议概况：**2012年11月8日至14日，中国共产党第十八次全国代表大会在北京举行。',
      '- 大会将科学发展观同马克思列宁主义、毛泽东思想、邓小平理论、“三个代表”重要思想一道确立为党的指导思想并写入党章。',
      '- 提出到2020年实现国内生产总值和城乡居民人均收入比2010年翻一番，全面建成小康社会。',
      '- 11月15日，十八届一中全会选举习近平为中共中央总书记。',
      '**史纲定位：**大会召开标志着我国进入全面建成小康社会决定性阶段，开启新时代强国战略的起点。',
    ].join('\n\n'),
    links: [{ label: '中国政府网', url: 'https://www.gov.cn' }],
    tags: ['十八大', '全面建成小康社会', '科学发展观'],
    time: { start: '2012-11-08', display: '2012年11月8日', precision: 'day' },
    ...AT('e-2012-cn', 0),
  },
  {
    id: 'e-2012-us',
    source: 'textbook',
    title: '奥巴马连任总统',
    countryId: 'usa',
    detail: [
      '**基本事实：**2012年11月6日（美国当地时间），民主党籍现任总统奥巴马赢得总统选举连任，继续担任美国第44任总统。',
      '- 奥巴马获得332张选举人票，共和党候选人罗姆尼获204张；民主党继续掌控参议院。',
      '- 连任后其政府继续推进“亚太再平衡”战略，并加快“跨太平洋伙伴关系协定”（TPP）谈判。',
      '**对照意义：**同一时期美国两党博弈加剧、政治极化显现，为理解十年后中美关系变化提供了背景。',
    ].join('\n\n'),
    tags: ['美国政治', '奥巴马', '大国关系'],
    time: { start: '2012-11-06', display: '2012年11月6日', precision: 'day' },
    ...AT('e-2012-us', 1),
  },
  {
    id: 'e-2012-world',
    source: 'textbook',
    title: '全球气候变化谈判持续推进',
    countryId: 'world',
    detail: [
      '**背景：**2009年哥本哈根会议后，全球气候治理陷入低潮，发达国家与发展中国家围绕减排义务分歧激烈。',
      '- 2012年11月底，联合国气候变化框架公约第18次缔约方会议在卡塔尔多哈举行。',
      '- 会议通过《多哈修正案》，确立《京都议定书》第二承诺期（2013—2020年），并启动德班平台谈判，目标是在2015年达成适用于所有缔约方的新气候协议。',
      '**考点提示：**中国坚持“共同但有区别的责任”原则，为次年的巴黎协定谈判奠定了基础。',
    ].join('\n\n'),
    tags: ['气候治理', '京都议定书', '多边主义'],
    time: { start: '2012-11-26', display: '2012年11月26日', precision: 'day' },
    ...AT('e-2012-world', 2),
  },
  {
    id: 'e-2013-us',
    source: 'textbook',
    title: '“亚太再平衡”战略推进',
    countryId: 'usa',
    detail: [
      '**战略动向：**2012年1月，奥巴马政府公布新军事战略，把亚太地区明确为美国外交与军事战略重心，推进“亚太再平衡”。',
      '- 美方计划到2020年前后把约60%的海军力量部署至亚太。',
      '- 同期美国强化与日本、韩国的军事同盟，举行大规模联合演习，并以TPP构建排斥中国的经贸规则圈。',
      '**教材关联：**美国战略东移使中国周边安全环境趋于复杂，构成共建“一带一路”、实施周边外交政策的时代背景之一。',
    ].join('\n\n'),
    tags: ['亚太再平衡', '中美关系', '周边外交'],
    time: { start: '2012-01-05', display: '2012年1月5日', precision: 'day' },
    ...AT('e-2013-us', 4),
  },
  {
    id: 'e-2013-world',
    source: 'textbook',
    title: '联合国启动2030年可持续发展议程制定',
    countryId: 'world',
    detail: [
      '**议程筹备：**《千年发展目标》（2000—2015年）即将到期，联合国于2013年启动“后2015发展议程”磋商。',
      '- 2013年联合国可持续发展开放工作组提出17项可持续发展目标（SDGs）建议方案。',
      '- 2015年9月，联合国可持续发展峰会通过《变革我们的世界：2030年可持续发展议程》。',
      '**答题要点：**2030年议程与《巴黎协定》构成当年全球治理的两大框架；中国成立落实2030年议程部际协调机制，脱贫攻坚与之深度对接。',
    ].join('\n\n'),
    links: [{ label: '联合国', url: 'https://www.un.org' }],
    tags: ['可持续发展', '联合国', '全球治理'],
    time: { start: '2013-03-14', display: '2013年3月14日', precision: 'day' },
    ...AT('e-2013-world', 5),
  },

  /* ------------------------------ 2013 ------------------------------ */
  {
    id: 'e-2013-cn',
    source: 'textbook',
    title: '提出共建“一带一路”倡议',
    countryId: 'china',
    detail: [
      '**提出过程：**2013年9月7日，习近平主席在哈萨克斯坦纳扎尔巴耶夫大学发表演讲，提出共同建设“丝绸之路经济带”；同年10月3日又在印度尼西亚国会演讲中提出共建“21世纪海上丝绸之路”。',
      '- 秉持共商共建共享原则，以政策沟通、设施联通、贸易畅通、资金融通、民心相通为主要内容。',
      '**意义：**倡议顺应经济全球化与沿线国家发展需求，成为中国扩大对外开放、参与全球治理的重大平台。',
    ].join('\n\n'),
    links: [{ label: '中国政府网', url: 'https://www.gov.cn' }],
    tags: ['一带一路', '对外开放', '新型国际关系'],
    time: { start: '2013-09-07', display: '2013年9月7日', precision: 'day' },
    ...AT('e-2013-cn', 3),
  },

  /* ------------------------------ 2015 ------------------------------ */
  {
    id: 'e-2015-world',
    source: 'textbook',
    title: '全球难民危机加剧',
    countryId: 'world',
    detail: [
      '**危机态势：**2015年欧洲出现二战以来最严重的难民危机。',
      '- 2015年9月，叙利亚男童伏尸土耳其海滩的照片震动欧洲，难民问题被推上舆论中心。',
      '- 叙利亚内战是主要根源，当年超过百万难民和移民经地中海进入欧洲。',
      '- 联合国难民署统计显示，全球被迫流离失所者年中首次突破6000万人。',
      '- 欧盟内部围绕分摊安置配额争执不下，德国“我们能做到”式开放政策引发多国争议。',
      '**答题指向：**难民危机暴露全球发展赤字与安全赤字，中国主张通过政治解决争端、促进发展消除根源。',
    ].join('\n\n'),
    tags: ['难民危机', '全球治理赤字', '人道主义'],
    time: { start: '2015-09-02', display: '2015年9月2日', precision: 'day' },
    ...AT('e-2015-world', 8),
  },
  {
    id: 'e-2015-eu',
    source: 'textbook',
    title: '巴黎气候大会达成《巴黎协定》',
    countryId: 'eu',
    detail: [
      '**协定通过：**2015年12月12日，联合国气候变化框架公约第21次缔约方会议在巴黎通过《巴黎协定》，近200个缔约方参与谈判。',
      '- 欧盟作为最大发达经济体集团力主通过有法律约束力的文本，主办国法国斡旋作用突出。',
      '- 协定将全球平均气温升幅控制在远低于2℃、努力限制在1.5℃以内，并确立“国家自主贡献”模式。',
      '**易混点：**《巴黎协定》2016年11月4日生效；中国与美国在大会前后两次发表联合声明，是推动通过的关键力量。',
    ].join('\n\n'),
    links: [{ label: '联合国气候变化框架公约', url: 'https://unfccc.int' }],
    tags: ['巴黎协定', '气候治理', '欧盟'],
    time: { start: '2015-12-12', display: '2015年12月12日', precision: 'day' },
    ...AT('e-2015-eu', 7),
  },
  {
    id: 'e-2015-cn',
    source: 'textbook',
    title: '亚投行正式成立',
    countryId: 'china',
    detail: [
      '**成立节点：**2015年12月25日，57个意向创始成员国代表在北京签署《亚洲基础设施投资银行协定》，亚洲基础设施投资银行正式成立。',
      '- 亚投行法定资本1000亿美元，总部设在北京，中国为第一大股东，出资投票权约26.06%。',
      '- 2016年6月亚投行正式开业，金立群出任首任行长。',
      '**考点：**这是中国首次主导多边金融机制，与“一带一路”相辅相成，体现中国提供国际公共能力的担当。',
    ].join('\n\n'),
    links: [{ label: '亚投行官网', url: 'https://www.aiib.org' }],
    tags: ['亚投行', '多边金融', '全球经济治理'],
    time: { start: '2015-12-25', display: '2015年12月25日', precision: 'day' },
    ...AT('e-2015-cn', 6),
  },

  /* ------------------------------ 2017 ------------------------------ */
  {
    id: 'e-2017-us',
    source: 'textbook',
    title: '美国宣布退出《巴黎协定》',
    countryId: 'usa',
    detail: [
      '**事件经过：**2017年6月1日，特朗普政府宣布美国退出《巴黎协定》，理由是协定“不公平”地束缚美国传统能源产业与就业。',
      '- 按协定条款，美国最早2019年11月才可提出退出，2020年11月4日正式生效。',
      '- 欧盟与中国发表联合声明重申坚定支持应对气候变化；美国20多个州组成“美国气候联盟”继续减排。',
      '**考查角度：**美国单边主义与中国坚持多边主义形成鲜明对照，是“全球治理变局”的典型材料。',
    ].join('\n\n'),
    tags: ['单边主义', '巴黎协定', '全球治理'],
    time: { start: '2017-06-01', display: '2017年6月1日', precision: 'day' },
    ...AT('e-2017-us', 10),
  },
  {
    id: 'e-2017-world',
    source: 'textbook',
    title: '全球贸易摩擦初现',
    countryId: 'world',
    detail: [
      '**摩擦苗头：**2017年“美国优先”政策由竞选口号转向施政实践。',
      '- 2017年8月，美方据《1974年贸易法》第301条款对中国的科技与知识产权政策发起正式调查。',
      '- 美国相继退出TPP、《巴黎协定》、联合国教科文组织等多边机制，美欧钢铝关税之争升温。',
      '- 世界贸易组织多边贸易体制遭遇冲击。',
      '**影响：**贸易保护主义抬头使中国外部压力增大，也成为后来中美经贸摩擦升级与加快科技自立自强的起点。',
    ].join('\n\n'),
    tags: ['贸易摩擦', '美国优先', '经济全球化'],
    time: { start: '2017-08-14', display: '2017年8月14日', precision: 'day' },
    ...AT('e-2017-world', 11),
  },
  {
    id: 'e-2017-cn',
    source: 'textbook',
    title: '党的十九大确立习近平新时代中国特色社会主义思想',
    countryId: 'china',
    detail: [
      '**理论成果：**2017年10月18日至24日，党的十九大在北京召开，报告为《决胜全面建成小康社会 夺取新时代中国特色社会主义伟大胜利》。',
      '- 大会把习近平新时代中国特色社会主义思想确立为党的指导思想并写入党章，2018年3月载入宪法。',
      '- 作出中国特色社会主义进入新时代的重大判断，指出我国社会主要矛盾已经转化为人民日益增长的美好生活需要和不平衡不充分的发展之间的矛盾。',
      '- 提出从2020年到本世纪中叶“两个阶段”的战略安排。',
    ].join('\n\n'),
    links: [{ label: '中国政府网', url: 'https://www.gov.cn' }],
    tags: ['十九大', '指导思想', '新时代', '主要矛盾'],
    time: { start: '2017-10-18', display: '2017年10月18日', precision: 'day' },
    ...AT('e-2017-cn', 9),
  },

  /* ------------------------------ 2020 ------------------------------ */
  {
    id: 'e-2020-world',
    source: 'textbook',
    title: '世界经济衰退与复苏',
    countryId: 'world',
    detail: [
      '**衰退图景：**国际货币基金组织估算，2020年世界经济萎缩约3.3%，为20世纪30年代大萧条以来最严重的年度衰退。',
      '- 主要经济体负增长，而中国全年经济增长2.3%，成为唯一实现正增长的主要经济体，GDP突破100万亿元。',
      '- 二十国集团推出缓减最贫穷国家债务的倡议，全球供应链产业链深度调整。',
      '**教材关联：**疫情冲击使世界百年未有之大变局加速演进，也凸显中国制度韧性，为“十四五”开局提供物质基础。',
    ].join('\n\n'),
    links: [{ label: '国际货币基金组织', url: 'https://www.imf.org' }],
    tags: ['世界经济', '疫情冲击', '中国之治'],
    time: { start: '2020-06-24', display: '2020年6月24日', precision: 'day' },
    ...AT('e-2020-world', 14),
  },
  {
    id: 'e-2020-us',
    source: 'textbook',
    title: '新冠疫情持续失控',
    countryId: 'usa',
    detail: [
      '**疫情与应对：**2019年12月新冠肺炎疫情发生，2020年1月30日世卫组织宣布国际关注的突发公共卫生事件，3月11日定性为全球大流行。',
      '- 美国疫情持续失控：截至2020年10月下旬累计确诊逾800万例、死亡逾20万例，居世界首位。',
      '- 联邦与州政府防控割裂，2020年美国宣布退出世界卫生组织，削弱全球抗疫合作。',
      '**对比考点：**中国坚持人民至上、生命至上，率先控制疫情并恢复经济；疫情成为检验各国治理效能的试金石。',
    ].join('\n\n'),
    links: [{ label: '世界卫生组织', url: 'https://www.who.int' }],
    tags: ['新冠疫情', '公共卫生', '治理效能'],
    time: { start: '2020-10-26', display: '2020年10月26日', precision: 'day' },
    ...AT('e-2020-us', 13),
  },
  {
    id: 'e-2020-cn',
    source: 'textbook',
    title: '脱贫攻坚战取得全面胜利',
    countryId: 'china',
    detail: [
      '**全面胜利：**2021年2月25日，全国脱贫攻坚总结表彰大会在北京举行，正式宣告脱贫攻坚战取得全面胜利。',
      '- 2020年底，现行标准下9899万农村贫困人口全部脱贫，832个贫困县全部摘帽，12.8万个贫困村全部出列，历史性解决绝对贫困。',
      '- 此前2020年10月26日至29日，党的十九届五中全会审议通过“十四五”规划建议，肯定脱贫攻坚成果，并部署同乡村振兴有效衔接。',
      '**考点：**这是全面建成小康社会的标志性指标，中国提前十年实现联合国减贫目标。',
    ].join('\n\n'),
    tags: ['脱贫攻坚', '全面小康', '乡村振兴'],
    time: { start: '2021-02-25', display: '2021年2月25日', precision: 'day' },
    ...AT('e-2020-cn', 12),
  },

  /* ------------------------------ 2022 ------------------------------ */
  {
    id: 'e-2022-ru',
    source: 'textbook',
    title: '俄乌冲突爆发',
    countryId: 'russia',
    detail: [
      '**冲突爆发：**2022年2月24日，俄罗斯对乌克兰发起“特别军事行动”，欧洲大陆冷战结束后最大规模地区武装冲突爆发。',
      '- 战事长期化：2022年9月底，俄宣布顿涅茨克、卢甘斯克、扎波罗热、赫尔松四地“入俄”，战局反复拉锯。',
      '- 联合国大会通过决议要求停火止战，美欧对俄多轮制裁，北约重新聚合。',
      '**中国立场：**发布全球安全倡议，主张共同、综合、合作、可持续的安全观，劝和促谈。',
    ].join('\n\n'),
    tags: ['俄乌冲突', '乌克兰危机', '全球安全倡议'],
    time: { start: '2022-02-24', display: '2022年2月24日', precision: 'day' },
    ...AT('e-2022-ru', 16),
  },
  {
    id: 'e-2022-world',
    source: 'textbook',
    title: '全球能源与粮食危机',
    countryId: 'world',
    detail: [
      '**危机表现：**乌克兰危机叠加疫情后供应链紊乱，引发全球性能源粮食紧张。',
      '- 世界银行估算2022年国际能源价格同比上涨约50%，欧洲天然气期价8月一度创下历史新高。',
      '- 联合国世界粮食计划署警告，全球数亿人面临严重粮食不安全。',
      '- 欧盟出台紧急节能与能源开支削减计划；中国端稳“饭碗”，全年粮食产量达1.37万亿斤并连续多年站稳1.3万亿斤以上。',
      '**答题指向：**危机再次证明粮食与能源安全是国家安全的基础，凸显国内大循环的战略底气。',
    ].join('\n\n'),
    tags: ['能源危机', '粮食安全', '供应链'],
    time: { start: '2022-08-26', display: '2022年8月26日', precision: 'day' },
    ...AT('e-2022-world', 17),
  },
  {
    id: 'e-2022-cn',
    source: 'textbook',
    title: '党的二十大召开，明确中国式现代化',
    countryId: 'china',
    detail: [
      '**开幕概况：**2022年10月16日，党的二十大在北京召开，报告题为《高举中国特色社会主义伟大旗帜 为全面建设社会主义现代化国家而团结奋斗》，擘画以中国式现代化全面推进中华民族伟大复兴的宏伟蓝图。',
      '- 明确中国式现代化是人口规模巨大、全体人民共同富裕、物质文明和精神文明相协调、人与自然和谐共生、走和平发展道路的现代化。',
      '- 重申2035年基本实现社会主义现代化的阶段性目标。',
      '**意义：**中国式现代化理论打破“现代化=西方化”迷思，为发展中国家走向现代化提供了全新选择。',
    ].join('\n\n'),
    links: [{ label: '中国政府网', url: 'https://www.gov.cn' }],
    tags: ['二十大', '中国式现代化', '民族复兴'],
    time: { start: '2022-10-16', display: '2022年10月16日', precision: 'day' },
    ...AT('e-2022-cn', 15),
  },

  /* ------------------------------ 2023 ------------------------------ */
  {
    id: 'e-2023-cn',
    source: 'textbook',
    title: '第三届“一带一路”国际合作高峰论坛举行',
    countryId: 'china',
    detail: [
      '**论坛盛况：**2023年10月17日至18日，第三届“一带一路”国际合作高峰论坛在北京举行，29位外国元首和政府首脑及联合国秘书长等出席，正值倡议提出十周年。',
      '- 论坛形成8大类458项成果；截至2023年，中国已与150多个国家和30多个国际组织签署合作文件。',
      '- 习近平主席主旨演讲宣布支持高质量共建“一带一路”的八项行动，强调把共建“一带一路”作为构建人类命运共同体的重要实践平台。',
      '**考点：**十年间“一带一路”从愿景变为行动，成为深受欢迎的国际公共产品和合作平台。',
    ].join('\n\n'),
    links: [{ label: '中国外交部', url: 'https://www.mfa.gov.cn' }],
    tags: ['一带一路', '高峰论坛', '人类命运共同体'],
    time: { start: '2023-10-17', display: '2023年10月17日', precision: 'day' },
    ...AT('e-2023-cn', 18),
  },
  {
    id: 'e-2023-us',
    source: 'textbook',
    title: '芯片出口管制升级',
    countryId: 'usa',
    detail: [
      '**管制升级：**2023年10月17日，美国商务部产业与安全局发布修订版先进计算芯片与半导体制造设备对华出口管制规则。',
      '- 新规进一步限制高性能人工智能芯片与高带宽存储器（HBM）对华出口，并堵住此前的部分技术漏洞。',
      '- 此前，日本、荷兰已就先进半导体设备出口与美国协调，对华管制相继生效。',
      '**影响：**美西方“小院高墙”式技术封锁冲击全球产业链；中国加快关键核心技术攻关，推动新型举国体制，自主替代进程提速。',
    ].join('\n\n'),
    tags: ['科技竞争', '出口管制', '自立自强'],
    time: { start: '2023-10-17', display: '2023年10月17日', precision: 'day' },
    ...AT('e-2023-us', 19),
  },
  {
    id: 'e-2023-world',
    source: 'textbook',
    title: '人工智能全球治理讨论升温',
    countryId: 'world',
    detail: [
      '**治理进程：**2023年生成式人工智能迅速普及，其风险与治理成为国际议程焦点。',
      '- 七国集团启动“广岛人工智能进程”；7月联大通过首份人工智能问题决议。',
      '- 欧盟《人工智能法案》于12月达成机构间临时协议。',
      '- 10月18日，中国政府发布《全球人工智能治理倡议》，主张各国人民共享人工智能发展和治理成果。',
      '**教材关联：**中国相继提出全球发展倡议、全球安全倡议、全球文明倡议与人工智能治理倡议，人类命运共同体理念向新领域延伸。',
    ].join('\n\n'),
    tags: ['人工智能', '全球治理', '中国方案'],
    time: { start: '2023-10-18', display: '2023年10月18日', precision: 'day' },
    ...AT('e-2023-world', 20),
  },
];
